<template>
  <HrPageLayout
    title="Department Appraisals"
    subtitle="Manage Appraisals"
    :breadcrumb-items="[
      { label: 'Performance' },
      { label: 'Manage Appraisals' }
    ]"
  >
    <div class="manager-dashboard-content">
      <!-- Manager Info Card -->
      <div v-if="managerInfo" class="hr-chart-card hr-mb-3" style="background: linear-gradient(135deg, #059669 0%, #16a34a 100%); border: none;">
        <div class="hr-chart-body" style="color: #fff;">
          <div class="hr-flex hr-justify-between hr-align-center hr-flex-wrap hr-gap-2">
            <div>
              <h5 class="hr-mb-1" style="color: #fff;">{{ managerInfo.name }}</h5>
              <p class="hr-mb-0" style="opacity: 0.95;">
                <strong>Position:</strong> {{ managerInfo.positionTitle }} |
                <strong>Department:</strong> {{ managerInfo.department }}
              </p>
            </div>
            <div class="hr-text-right">
              <h3 class="hr-mb-0" style="color: #fff;">{{ statistics.total }}</h3>
              <small style="opacity: 0.9;">Total Appraisals</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Statistics Cards -->
      <div class="hr-metrics-grid">
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <p class="hr-metric-label">Pending Review</p>
          <h4 class="hr-metric-value">{{ statistics.pending }}</h4>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <p class="hr-metric-label">Completed</p>
          <h4 class="hr-metric-value">{{ statistics.completed }}</h4>
        </div>
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-loader-circle"></i></div>
          <p class="hr-metric-label">In Progress</p>
          <h4 class="hr-metric-value">{{ statistics.inProgress }}</h4>
        </div>
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-star"></i></div>
          <p class="hr-metric-label">Avg Rating</p>
          <h4 class="hr-metric-value">{{ statistics.avgRating }}</h4>
        </div>
      </div>

      <!-- Create Appraisal Button -->
      <div class="hr-mb-3">
        <button @click="openCreateAppraisalModal" class="hr-btn hr-btn-primary">
          <i class="bx bx-plus"></i> Create Appraisal
        </button>
      </div>

      <!-- Filters -->
      <div class="hr-chart-card hr-mb-3">
        <div class="hr-chart-body">
          <div class="hr-form-grid" style="grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); align-items: end; gap: 1rem;">
            <div>
              <label class="hr-form-label">Filter by Status</label>
              <select v-model="filters.status" @change="loadAppraisals" class="hr-select">
                <option value="">All Statuses</option>
                <option value="Self-Assessment Complete">Pending My Review</option>
                <option value="Appraiser Review Complete">Reviewed by Me</option>
                <option value="Reviewer Complete">Final Review Done</option>
                <option value="Approved">Approved</option>
                <option value="Draft">Draft</option>
              </select>
            </div>
            <div>
              <label class="hr-form-label">Filter by Year</label>
              <select v-model="filters.year" @change="loadAppraisals" class="hr-select">
                <option value="">All Years</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
                <option value="2023">2023</option>
              </select>
            </div>
            <div>
              <label class="hr-form-label">Search Employee</label>
              <input
                type="text"
                v-model="filters.search"
                @input="searchAppraisals"
                class="hr-input"
                placeholder="Search by employee name..."
              >
            </div>
            <div>
              <button @click="clearFilters" class="hr-btn hr-btn-secondary hr-w-100">
                <i class="bx bx-refresh"></i> Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Appraisals Table -->
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title">Department Appraisals</h5>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p>Loading appraisals...</p>
          </div>

          <div v-else-if="filteredAppraisals.length > 0" class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Position</th>
                  <th>Period</th>
                  <th>Year</th>
                  <th>Status</th>
                  <th>Self Rating</th>
                  <th>My Rating</th>
                  <th>Submitted</th>
                  <th class="hr-text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="appraisal in filteredAppraisals" :key="appraisal.id">
                  <td>
                    <div class="hr-flex hr-align-center hr-gap-2">
                      <span class="hr-avatar-initials hr-bg-primary hr-text-white">{{ getInitials(appraisal.employeeName) }}</span>
                      <span class="hr-fw-bold">{{ appraisal.employeeName }}</span>
                    </div>
                  </td>
                  <td>{{ appraisal.positionTitle }}</td>
                  <td><small>{{ appraisal.periodStart }} - {{ appraisal.periodEnd }}</small></td>
                  <td>{{ appraisal.appraisalYear }}</td>
                  <td>
                    <span :class="['hr-badge', getStatusBadgeClass(appraisal.status)]">{{ appraisal.status }}</span>
                  </td>
                  <td>
                    <span v-if="appraisal.sectionA_SelfOverallRating" class="hr-badge hr-badge-primary">
                      {{ parseFloat(appraisal.sectionA_SelfOverallRating).toFixed(2) }}
                    </span>
                    <span v-else class="hr-text-muted">-</span>
                  </td>
                  <td>
                    <span v-if="appraisal.sectionA_AppraiserOverallRating" class="hr-badge hr-badge-success">
                      {{ parseFloat(appraisal.sectionA_AppraiserOverallRating).toFixed(2) }}
                    </span>
                    <span v-else class="hr-text-muted">Pending</span>
                  </td>
                  <td><small>{{ appraisal.submittedDate || '-' }}</small></td>
                  <td class="hr-text-center">
                    <div class="hr-flex hr-gap-1 hr-justify-center">
                      <button
                        v-if="appraisal.status === 'Draft'"
                        @click="setKpis(appraisal.id)"
                        class="hr-btn hr-btn-sm hr-btn-outline-warning"
                        title="Set KPIs for this appraisal"
                      >
                        <i class="bx bx-target-lock"></i> Set KPIs
                      </button>
                      <button
                        @click="viewAppraisal(appraisal.id)"
                        class="hr-btn hr-btn-sm hr-btn-outline-primary"
                        title="View Details"
                      >
                        <i class="bx bx-show"></i>
                      </button>
                      <button
                        v-if="appraisal.status === 'Self-Assessment Complete' || appraisal.status === 'Appraiser Review Pending'"
                        @click="reviewAppraisal(appraisal.id)"
                        class="hr-btn hr-btn-sm hr-btn-outline-primary"
                        title="Review & Rate"
                      >
                        <i class="bx bx-edit"></i> Review
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="hr-empty-state">
            <i class="hr-empty-icon bx bx-folder-open"></i>
            <h5>No Appraisals Found</h5>
            <p class="hr-text-muted">There are no appraisals matching your filters.</p>
            <button @click="openCreateAppraisalModal" class="hr-btn hr-btn-primary">
              <i class="bx bx-plus"></i> Create Appraisal for Team Member
            </button>
          </div>
        </div>
      </div>

      <!-- Create Appraisal Modal (pure Vue) -->
      <div v-if="showCreateModal" class="hr-modal-overlay" @click.self="closeCreateAppraisalModal">
        <div class="hr-modal-dialog">
          <div class="hr-modal-content">
            <div class="hr-modal-header">
              <h5 class="hr-modal-title">Create Appraisal</h5>
              <button type="button" class="hr-modal-close" @click="closeCreateAppraisalModal" aria-label="Close">
                <i class="bx bx-x"></i>
              </button>
            </div>
            <div class="hr-modal-body">
              <div class="hr-mb-3">
                <label class="hr-form-label">Employee <span class="hr-text-danger">*</span></label>
                <select v-model="createForm.employeeId" class="hr-select" @change="onCreateEmployeeChange" required>
                  <option value="">Select team member</option>
                  <option v-for="emp in teamMembers" :key="emp.id" :value="emp.id">{{ emp.fullName }} ({{ emp.positionTitle }})</option>
                </select>
                <small v-if="!teamMembers.length && teamLoaded" class="hr-form-hint">No team members available or all have appraisals for this period.</small>
              </div>
              <div class="hr-form-grid" style="grid-template-columns: 1fr 1fr;">
                <div>
                  <label class="hr-form-label">Period Start <span class="hr-text-danger">*</span></label>
                  <input v-model="createForm.periodStart" type="date" class="hr-input" required @change="loadTeamMembers">
                </div>
                <div>
                  <label class="hr-form-label">Period End <span class="hr-text-danger">*</span></label>
                  <input v-model="createForm.periodEnd" type="date" class="hr-input" required @change="loadTeamMembers">
                </div>
              </div>
            </div>
            <div class="hr-modal-footer">
              <button type="button" class="hr-btn hr-btn-secondary" @click="closeCreateAppraisalModal">Cancel</button>
              <button type="button" class="hr-btn hr-btn-primary" :disabled="createLoading || !createForm.employeeId || !createForm.periodStart || !createForm.periodEnd" @click="createAppraisal">
                <span v-if="createLoading" class="hr-spinner hr-spinner-sm hr-mr-1"></span>
                Create
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";

export default {
  name: "ManagerDashboard",

  components: {
    HrPageLayout
  },

  data() {
    return {
      loading: false,
      managerInfo: null,
      appraisals: [],
      filteredAppraisals: [],
      filters: {
        status: '',
        year: '',
        search: ''
      },
      statistics: {
        total: 0,
        pending: 0,
        completed: 0,
        inProgress: 0,
        avgRating: '0.00'
      },
      showCreateModal: false,
      createForm: { employeeId: '', periodStart: '', periodEnd: '' },
      teamMembers: [],
      teamLoaded: false,
      createLoading: false
    };
  },

  mounted() {
    this.loadManagerInfo();
  },

  methods: {
    async loadManagerInfo() {
      try {
        const data = new FormData();
        data.append("function", "getManagerInfo");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.managerInfo = res.data.data;
          this.loadAppraisals();
        } else {
          this.$toast?.error("Failed to load manager info") || alert("Failed to load manager info");
        }
      } catch (error) {
        console.error("Failed to load manager info:", error);
        this.$toast?.error("Error loading manager info") || alert("Error loading manager info");
      }
    },

    async loadAppraisals() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getDepartmentAppraisals");
        data.append("status", this.filters.status);
        data.append("year", this.filters.year);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.appraisals = res.data.data;
          this.applyFilters();
          this.calculateStatistics();
        } else {
          this.appraisals = [];
        }
      } catch (error) {
        console.error("Failed to load appraisals:", error);
        this.$toast?.error("Failed to load appraisals") || alert("Failed to load appraisals");
      } finally {
        this.loading = false;
      }
    },

    applyFilters() {
      let filtered = [...this.appraisals];

      if (this.filters.search) {
        const search = this.filters.search.toLowerCase();
        filtered = filtered.filter(a =>
            a.employeeName.toLowerCase().includes(search) ||
            a.positionTitle.toLowerCase().includes(search)
        );
      }

      this.filteredAppraisals = filtered;
    },

    searchAppraisals() {
      this.applyFilters();
    },

    clearFilters() {
      this.filters = {
        status: '',
        year: '',
        search: ''
      };
      this.loadAppraisals();
    },

    calculateStatistics() {
      this.statistics.total = this.appraisals.length;

      this.statistics.pending = this.appraisals.filter(a =>
          a.status === 'Self-Assessment Complete' || a.status === 'Appraiser Review Pending'
      ).length;

      this.statistics.completed = this.appraisals.filter(a =>
          a.status === 'Appraiser Review Complete' || a.status === 'Approved'
      ).length;

      this.statistics.inProgress = this.appraisals.filter(a =>
          a.status === 'Reviewer Pending' || a.status === 'HR/CEO Review Pending'
      ).length;

      const ratingsWithValues = this.appraisals.filter(a => a.sectionA_AppraiserOverallRating);
      if (ratingsWithValues.length > 0) {
        const sum = ratingsWithValues.reduce((acc, a) => acc + parseFloat(a.sectionA_AppraiserOverallRating), 0);
        this.statistics.avgRating = (sum / ratingsWithValues.length).toFixed(2);
      } else {
        this.statistics.avgRating = '0.00';
      }
    },

    viewAppraisal(id) {
      this.$router.push({
        path: '/manager-appraisal-view',
        query: { id: id }
      });
    },

    reviewAppraisal(id) {
      this.$router.push({
        path: '/manager-review-form',
        query: { id: id }
      });
    },

    setKpis(id) {
      this.$router.push({
        path: '/manager-appraisal-view',
        query: { id: id, setKpis: 1 }
      });
    },

    getStatusBadgeClass(status) {
      const classes = {
        'Draft': 'hr-badge-secondary',
        'Self-Assessment Complete': 'hr-badge-warning',
        'Appraiser Review Pending': 'hr-badge-warning',
        'Appraiser Review Complete': 'hr-badge-info',
        'Reviewer Pending': 'hr-badge-info',
        'Reviewer Complete': 'hr-badge-primary',
        'HR/CEO Review Pending': 'hr-badge-primary',
        'Approved': 'hr-badge-success',
        'Rejected': 'hr-badge-danger'
      };
      return classes[status] || 'hr-badge-secondary';
    },

    getInitials(name) {
      if (!name) return '?';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    openCreateAppraisalModal() {
      this.showCreateModal = true;
      this.createForm = {
        employeeId: '',
        periodStart: this.getDefaultPeriodStart(),
        periodEnd: this.getDefaultPeriodEnd()
      };
      this.loadTeamMembers();
    },
    closeCreateAppraisalModal() {
      this.showCreateModal = false;
      this.createForm = { employeeId: '', periodStart: '', periodEnd: '' };
      this.teamMembers = [];
    },
    getDefaultPeriodStart() {
      const d = new Date();
      d.setMonth(d.getMonth() - 3);
      return d.toISOString().split('T')[0];
    },
    getDefaultPeriodEnd() {
      return new Date().toISOString().split('T')[0];
    },
    onCreateEmployeeChange() {
      // Could refresh team if needed
    },
    async loadTeamMembers() {
      this.teamLoaded = false;
      try {
        const data = new FormData();
        data.append("function", "getManagerTeamMembers");
        if (this.createForm.periodStart) data.append("periodStart", this.createForm.periodStart);
        if (this.createForm.periodEnd) data.append("periodEnd", this.createForm.periodEnd);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success && Array.isArray(res.data.data)) {
          this.teamMembers = res.data.data;
        } else {
          this.teamMembers = [];
        }
      } catch (e) {
        this.teamMembers = [];
      } finally {
        this.teamLoaded = true;
      }
    },
    async createAppraisal() {
      if (!this.createForm.employeeId || !this.createForm.periodStart || !this.createForm.periodEnd) return;
      this.createLoading = true;
      try {
        const data = new FormData();
        data.append("function", "createAppraisal");
        data.append("employeeId", this.createForm.employeeId);
        data.append("periodStart", this.createForm.periodStart);
        data.append("periodEnd", this.createForm.periodEnd);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success?.("Appraisal created") || alert("Appraisal created");
          this.closeCreateAppraisalModal();
          this.loadAppraisals();
        } else {
          this.$toast?.error?.(res?.data?.message || "Failed to create") || alert(res?.data?.message || "Failed to create");
        }
      } catch (e) {
        this.$toast?.error?.("Failed to create appraisal") || alert("Failed to create appraisal");
      } finally {
        this.createLoading = false;
      }
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.hr-mb-3 { margin-bottom: 1rem; }
.hr-mr-1 { margin-right: 0.25rem; }
.hr-w-100 { width: 100%; }
.hr-avatar-initials { height: 40px; width: 40px; min-width: 40px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-weight: 600; font-size: 0.875rem; }
.hr-fw-bold { font-weight: 600; }
</style>
